import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import type { Position } from '__generated__/globalTypes';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { Stadium } from 'components/animations/Stadium';
import { CardImg } from 'components/card/CardImg';
import { useTransitionApi } from 'hooks/ui/useTransitionApi';
import { randomIntFromInterval } from 'lib/math';

import { Result } from '../Result';
import { Team } from '../Team';
import type { PvEImplementation_anyCardInterface } from './__generated__/index.graphql';

export enum TITLE {
  FOOTBALL_BUNDESLIGA = 'Bundesliga',
  FOOTBALL_LALIGA = 'LALIGA',
  FOOTBALL_PREMIER_LEAGUE = 'Premier League',

  NBA_WESTERN = 'Western',
  NBA_EASTERN = 'Eastern',
  NBA_ALL_STAR = 'AllStar',
}

type PositionMapItem = {
  position: Position;
  label?: MessageDescriptor;
};

type TeamItem = PvEImplementation_anyCardInterface;

type Props = {
  playersData: TeamItem[][];
  fieldUrl: string;
  title: TITLE;
  laligaSorare?: string;
  laligaEASports?: string;
  onClick: () => void;
  positionsMap: PositionMapItem[];
  popupBackground: string;
  fullHeight?: boolean;
};

const appearIn = keyframes`
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  50% {
    scale: 1.1;
  }
  100% {
    scale: 1;
  }
`;

const pulse = keyframes`
    0% {
        transform: scale(0.98);
    }

    50% {
        transform: scale(1.02);
    }

    100% {
        transform: scale(0.98);
    }
`;

const pulsate = keyframes`
    0% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.2);
        opacity: 0;
    }

    100% {
        transform: scale(1);
        opacity: 0;
    }
`;

const Root = styled(Vertical)`
  position: relative;
  z-index: 1;
  justify-content: space-between;
  height: var(--100vh);
  overflow: hidden;
  &.fullHeight {
    height: 100%;
  }
`;

const Content = styled(Vertical)`
  width: 500px;
  max-width: 100%;
  margin: auto;
  justify-content: space-around;
  flex: 1;
`;

const Heading = styled(Vertical)`
  bottom: 100%;
  text-align: center;
  width: 100%;
`;

const StyledHeading = styled.h2`
  font-size: 3.2vmin;
`;

const StyledBody = styled.p`
  font-size: 1.8vmin;
  color: var(--c-nd-600);
`;

const CenterContent = styled(Horizontal)`
  padding: 0 var(--double-unit);
  margin: 5% 0;
  width: 100%;
  column-gap: 25%;
  justify-content: space-around;
`;

const Pick = styled.div`
  animation: 0.3s ease ${appearIn} forwards;
  animation: ${pulse} 1s 4s infinite;
`;

const PickButton = styled(ButtonBase)`
  position: relative;
  max-width: calc(30 * var(--vw));
  flex: 1;
`;

const Pulsation = styled.span`
  inset: 0;
  border: 2px solid white;
  border-radius: var(--unit);
  position: absolute;
  animation: ${pulsate} 2s infinite;
  aspect-ratio: var(--card-aspect-ratio);
  opacity: 0;
`;

const StadiumWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  bottom: -25vmax;
  pointer-events: none;
`;

const LogoContainer = styled(Horizontal).attrs({ center: true })`
  padding-bottom: var(--double-unit);
`;

const LaligaLogo = styled.img`
  width: clamp(12px, 6rem, 120px);
  height: auto;
`;

export const PvEImplementation = ({
  playersData,
  fieldUrl,
  title,
  laligaEASports,
  laligaSorare,
  onClick,
  positionsMap,
  popupBackground,
  fullHeight = false,
}: Props) => {
  const { formatMessage } = useIntl();
  const { updateDOM } = useTransitionApi();
  const isAIPick = false;
  const [state, setState] = useState<
    Record<'yourTeam' | 'opponentTeam', PvEImplementation_anyCardInterface[]>
  >({
    yourTeam: [],
    opponentTeam: [],
  });
  const [currentSelection, setCurrentSelection] = useState(0);

  const messages = defineMessages({
    yourTeam: {
      id: 'nba.minigames.pve_.yourTeam',
      defaultMessage: 'Your Team',
    },
    opponentTeam: {
      id: 'nba.minigames.pve_.opponentTeam',
      defaultMessage: 'Your opponent',
    },
  });
  const displayResult = state.yourTeam.length === 5;

  const selectionData = playersData[currentSelection];

  const yourScore = state.yourTeam.reduce(
    (acc, cur) => (cur.score || 0) + acc,
    0
  );
  const opponentScore = state.opponentTeam.reduce(
    (acc, cur) => (cur.score || 0) + acc,
    0
  );

  let heading;
  if (
    [
      TITLE.FOOTBALL_LALIGA,
      TITLE.FOOTBALL_BUNDESLIGA,
      TITLE.FOOTBALL_PREMIER_LEAGUE,
    ].includes(title)
  ) {
    heading = (
      <Heading gap={0}>
        <StyledHeading>
          <FormattedMessage
            id="football.minigames.pve_.Picker.title"
            defaultMessage="Think you know football?"
          />
          <br />
          <FormattedMessage
            id="football.minigames.pve_.Picker.subtitle"
            defaultMessage="Pick the most in-form player!"
          />
        </StyledHeading>
        <StyledBody>
          <FormattedMessage
            id="football.minigames.pve_.Picker.description"
            defaultMessage="Scoring is based on performance in the last 5 games"
          />
        </StyledBody>
      </Heading>
    );
  } else {
    heading = (
      <Heading gap={0}>
        <StyledHeading>
          <FormattedMessage
            id="nba.minigames.pve_.Picker.title"
            defaultMessage="Think you know ball?"
          />
          <br />
          {title === TITLE.NBA_ALL_STAR ? (
            <FormattedMessage
              id="nba.minigames.pve_.Picker.subtitle.allStar"
              defaultMessage="Pick the best All Star roster"
            />
          ) : (
            <FormattedMessage
              id="nba.minigames.pve_.Picker.subtitle"
              defaultMessage="Pick your dream {title} Conference roster"
              values={{
                title,
              }}
            />
          )}
        </StyledHeading>
        <StyledBody>
          <FormattedMessage
            id="nba.minigames.pve_.Picker.description"
            defaultMessage="Scores are based on real stats from the past 5 games"
          />
        </StyledBody>
      </Heading>
    );
  }
  return (
    <Root className={classNames({ fullHeight })}>
      <Content gap={0}>
        <Team
          team={state.opponentTeam}
          displayResult={displayResult}
          label={formatMessage(messages.opponentTeam)}
          currentSelection={currentSelection}
          positionsMap={positionsMap}
        />
        {displayResult ? (
          <Result
            showResult={displayResult}
            yourTeamScore={yourScore}
            opponentTeamScore={opponentScore}
            popupBackground={popupBackground}
            onClick={onClick}
          />
        ) : (
          <>
            {heading}
            <CenterContent>
              <PickButton
                onClick={() => {
                  updateDOM(() => {
                    setState(prevState => ({
                      yourTeam: prevState.yourTeam.concat(selectionData[0]),
                      opponentTeam: prevState.opponentTeam.concat(
                        selectionData[
                          isAIPick ? randomIntFromInterval(0, 1) : 1
                        ]
                      ),
                    }));
                    setCurrentSelection(prevState => prevState + 1);
                  });
                }}
              >
                <Pick key={currentSelection}>
                  <CardImg
                    src={selectionData[0].pictureUrl}
                    style={{
                      viewTransitionName: `card-${selectionData[0].slug}`,
                    }}
                    width={320}
                  />
                </Pick>
                {currentSelection === 0 && <Pulsation />}
              </PickButton>
              <PickButton
                onClick={() => {
                  updateDOM(() => {
                    setState(prevState => ({
                      yourTeam: prevState.yourTeam.concat(selectionData[1]),
                      opponentTeam: prevState.opponentTeam.concat(
                        selectionData[
                          isAIPick ? randomIntFromInterval(0, 1) : 0
                        ]
                      ),
                    }));
                    setCurrentSelection(prevState => prevState + 1);
                  });
                }}
              >
                <Pick key={currentSelection}>
                  <CardImg
                    src={selectionData[1].pictureUrl}
                    style={{
                      viewTransitionName: `card-${selectionData[1].slug}`,
                    }}
                    width={320}
                  />
                </Pick>
                {currentSelection === 0 && <Pulsation />}
              </PickButton>
            </CenterContent>
          </>
        )}
        <Team
          viewTransition
          label={formatMessage(messages.yourTeam)}
          team={state.yourTeam}
          displayResult={displayResult}
          currentSelection={currentSelection}
          positionsMap={positionsMap}
        />
        {title === 'LALIGA' && (
          <LogoContainer center>
            <LaligaLogo src={laligaSorare} alt="" />
            <LaligaLogo src={laligaEASports} alt="" />
          </LogoContainer>
        )}
      </Content>
      <StadiumWrapper>
        <Stadium
          fieldUrl={fieldUrl}
          completion={Math.max(0.2 * state.yourTeam.length, 0.19)}
        />
      </StadiumWrapper>
    </Root>
  );
};

PvEImplementation.fragments = {
  anyCardInterface: gql`
    fragment PvEImplementation_anyCardInterface on AnyCardInterface {
      slug
      anyPositions
      pictureUrl
      score: averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
      ...PvETeam_anyCard
    }
    ${Team.fragments.team}
  ` as TypedDocumentNode<PvEImplementation_anyCardInterface>,
};
